/**
 * `window.__game`: the debug API for agent-driven checks (06 §8). Read the state, move the
 * ranger, step the mission by hand, save a frame. Nothing in the game itself calls it.
 */

import type { DebugLayers } from '../render/debug/DebugLayers';
import type { Command } from '../sim/types';
import { hashWorld, type Sim } from '../sim/world';

export interface DebugHooks {
  /** Step `ticks` of the sim with this command held, then draw one frame. */
  advance(ticks: number, cmd: Partial<Command>): void;
  /**
   * Step `ticks` on the PLAYER's input - the keys and mouse as the page has them - then draw one frame: the whole
   * path from DOM events to commands, for a background tab that never gets an animation frame.
   */
  play(ticks: number): void;
  /** Where a world point (x, y up, z) is drawn, in viewport CSS pixels; null when off the picture. */
  screenOf(x: number, y: number, z: number): { x: number; y: number } | null;
  save(name: string): unknown;
  /** On a replay: null while it plays, then whether it matched the recording. */
  verdict(): string | null;
  /** Switch a debug layer: nav, colliders, vision, detection, noise, paths. */
  layer(name: Parameters<DebugLayers['setEnabled']>[0], on: boolean): void;
  /** Post-process options: nvg, nightPenalty, crt, flashCap. */
  look(options: Record<string, number | boolean>): void;
}

export function installDebugApi(sim: Sim, hooks: DebugHooks): void {
  const w = (): Sim['world'] => sim.world;
  Object.assign(window, {
    __game: {
      sim,
      hash: () => hashWorld(w()),
      state: () => ({
        tick: w().tick, phase: w().insertion.phase, clock: Math.round(w().clockTicks / 60), ranger: w().ranger,
        alert: w().alert, osprey: w().osprey, objective: w().objective, result: w().result,
      }),
      enemies: () => w().enemies.map((e) => ({
        id: e.id, kind: e.kind, persona: e.persona, state: e.state, role: e.role,
        x: +e.x.toFixed(1), z: +e.z.toFixed(1), det: +e.detection.toFixed(2),
      })),
      teleport: (x: number, z: number) => {
        w().ranger.x = x;
        w().ranger.z = z;
      },
      give: () => {
        Object.assign(w().ranger.inventory, { magazines: 12, grenades: 9, laws: 5, bombs: 5, firstAid: 5 });
      },
      advance: (ticks: number, cmd: Partial<Command> = {}) => hooks.advance(ticks, cmd),
      play: (ticks: number) => hooks.play(ticks),
      screenOf: (x: number, y: number, z: number) => hooks.screenOf(x, y, z),
      save: (name: string) => hooks.save(name),
      verdict: () => hooks.verdict(),
      layer: (name: Parameters<DebugLayers['setEnabled']>[0], on = true) => hooks.layer(name, on),
      look: (options: Record<string, number | boolean>) => hooks.look(options),
    },
  });
}
